import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { addAlert } from '../../alerts';
import { prompt, deletePrompt } from '../../prompts';
import { sendPostRequest, sendPatchRequest } from '../../utils';

// --- Constants & Config ---
const ENDPOINTS = {
    MATRIX: '/api/users/permissions-matrix/',
    GROUPS: '/api/users/groups/',
    UPDATE_PERMS: '/api/users/update-permissions/',
};

const DOM_IDS = {
    TABLE: 'permissions-matrix-table',
    BTN_SAVE: 'save-permissions-btn',
    BTN_ADD_GROUP: 'add-group-btn',
};

const CSS_CLASSES = {
    CHANGED: 'is-changed',
    ASSIGNED: 'perm-assigned',
    UNASSIGNED: 'perm-unassigned',
};

// --- State Management ---
const state = {
    tabulator: null,
    pendingChanges: {},
    elements: {
        saveBtn: null,
        addBtn: null,
        table: null,
    },

    get hasChanges() {
        return Object.keys(this.pendingChanges).length > 0;
    },

    addChange(groupId, permCodename, isAssigned) {
        const key = `group_${groupId}_perm_${permCodename}`;
        this.pendingChanges[key] = {
            group_id: groupId,
            permission_codename: permCodename,
            is_assigned: isAssigned,
        };
        this.updateUiState();
    },

    clearChanges() {
        this.pendingChanges = {};
        this.updateUiState();
    },

    updateUiState() {
        if (!this.elements.saveBtn) return;
        this.elements.saveBtn.disabled = !this.hasChanges;
    },
};

// --- API Layer ---
const api = {
    async fetchMatrix() {
        const response = await fetch(ENDPOINTS.MATRIX);
        if (!response.ok)
            throw new Error(gettext('Nie udało się wczytać danych.'));
        return response.json();
    },

    async createGroup(name) {
        const response = await sendPostRequest(ENDPOINTS.GROUPS, { name });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(gettext('Błąd tworzenia grupy.'));
        }
        return data;
    },

    async savePermissions(changes) {
        const response = await sendPostRequest(ENDPOINTS.UPDATE_PERMS, changes);
        return response.json();
    },
};

// --- Tabulator Formatters & Actions ---

const permissionStateFormatter = (cell) => {
    const isAssigned = cell.getValue();
    const element = cell.getElement();

    element.classList.remove(CSS_CLASSES.ASSIGNED, CSS_CLASSES.UNASSIGNED);

    const iconClass = isAssigned ? 'fa-check' : 'fa-times';
    const colorClass = isAssigned
        ? CSS_CLASSES.ASSIGNED
        : CSS_CLASSES.UNASSIGNED;

    element.classList.add(colorClass);
    return `<i class="fas ${iconClass}"></i>`;
};

const handleCellClick = (e, cell) => {
    const nextValue = !cell.getValue();
    const rowData = cell.getRow().getData();
    const colDef = cell.getColumn().getDefinition();

    cell.setValue(nextValue, true);
    cell.getElement().classList.add(CSS_CLASSES.CHANGED);

    state.addChange(colDef._group_id, rowData.codename, nextValue);
};

// --- Process Logic ---

async function handleCreateGroup() {
    const result = await prompt({
        title: gettext('Nowa Grupa'),
        text: gettext('Podaj nazwę dla nowej grupy użytkowników:'),
        input: 'text',
    });

    if (!result.isConfirmed || !result.value) return;

    if (result.value.length > 150) {
        addAlert(
            'warning',
            gettext('Nazwa grupy może mieć maksymalnie 150 znaków.'),
        );
        return;
    }

    try {
        const data = await api.createGroup(result.value);
        addAlert(
            'success',
            data.message || gettext('Grupa została utworzona.'),
        );
        refreshTable();
    } catch (e) {
        console.error(e);
        addAlert('error', e.message || gettext('Błąd tworzenia grupy.'));
    }
}

async function handleRenameGroup(e, column) {
    const { title: groupName, _group_id: groupId } = column.getDefinition();

    const result = await prompt({
        title: gettext('Zmień nazwę grupy'),
        text: gettext('Podaj nową nazwę dla grupy:'),
        input: 'text',
        inputValue: groupName,
    });

    if (!result.isConfirmed || !result.value || result.value === groupName)
        return;

    if (result.value.length > 150) {
        addAlert(
            'warning',
            gettext('Nazwa grupy może mieć maksymalnie 150 znaków.'),
        );
        return;
    }

    try {
        await sendPatchRequest(`${ENDPOINTS.GROUPS}${groupId}/`, {
            name: result.value,
        });
        addAlert('success', gettext('Nazwa grupy została zmieniona.'));
        refreshTable();
    } catch (error) {
        addAlert('error', error.message || gettext('Błąd zmiany nazwy grupy.'));
        console.error(error);
    }
}

function handleDeleteGroup(e, column) {
    const { title: groupName, _group_id: groupId } = column.getDefinition();

    deletePrompt({
        options: {
            title: gettext('Usunąć grupę?'),
            html: gettext(
                `Czy na pewno chcesz usunąć grupę <strong>${groupName}</strong>?<br><br>Ta operacja jest nieodwracalna.`,
            ),
        },
        url: `${ENDPOINTS.GROUPS}${groupId}/`,
        successAction: () => {
            addAlert('success', gettext('Grupa została usunięta.'));
            refreshTable();
        },
        failedAction: () => {
            addAlert(
                'error',
                gettext(
                    'Nie udało się usunąć grupy. ' +
                        'Nie można usunąć grupy, do której przypisani są użytkownicy.',
                ),
            );
        },
    });
}

async function handleSavePermissions() {
    if (!state.hasChanges) return;

    const btn = state.elements.saveBtn;

    btn.disabled = true;

    try {
        const changesList = Object.values(state.pendingChanges);
        const data = await api.savePermissions(changesList);

        addAlert('success', data.message);
        state.clearChanges();
        refreshTable();
    } catch (e) {
        console.error(e);
        addAlert('error', e.message || gettext('Błąd zapisu.'));
        btn.disabled = false;
    }
}

// --- Table Configuration ---

function buildColumns(groups) {
    const baseColumn = {
        title: gettext('Uprawnienie'),
        field: 'name',
        frozen: true,
        minWidth: 350,
        tooltip: (e, cell) => cell.getRow().getData().description,
    };

    const groupColumns = groups.map((group) => ({
        title: group.name,
        field: `group_states.group_${group.id}`,
        hozAlign: 'center',
        formatter: permissionStateFormatter,
        cellClick: handleCellClick,
        headerSort: false,
        resizable: false,
        _group_id: group.id,
        headerMenuIcon: `<i class="fas fa-ellipsis-v"></i>`,
        headerMenu: [
            {
                label: `<i class="fas fa-pencil-alt mr-10"></i> ${gettext(
                    'Zmień nazwę',
                )}`,
                action: handleRenameGroup,
            },
            {
                label: `<i class="fas fa-trash-alt mr-10"></i> ${gettext(
                    'Usuń grupę',
                )}`,
                action: handleDeleteGroup,
            },
        ],
    }));

    return [baseColumn, ...groupColumns];
}

function refreshTable() {
    if (state.tabulator) {
        state.tabulator.destroy();
        state.tabulator = null;
    }
    initializePermissionsMatrix();
}

// --- Initialization ---

function bindEvents() {
    if (state.elements.saveBtn) {
        const newSaveBtn = state.elements.saveBtn.cloneNode(true);
        state.elements.saveBtn.parentNode.replaceChild(
            newSaveBtn,
            state.elements.saveBtn,
        );
        state.elements.saveBtn = newSaveBtn;
        state.elements.saveBtn.addEventListener('click', handleSavePermissions);
    }

    if (state.elements.addBtn) {
        const newAddBtn = state.elements.addBtn.cloneNode(true);
        state.elements.addBtn.parentNode.replaceChild(
            newAddBtn,
            state.elements.addBtn,
        );
        state.elements.addBtn = newAddBtn;
        state.elements.addBtn.addEventListener('click', handleCreateGroup);
    }
}

export default async function initializePermissionsMatrix() {
    state.elements.table = document.getElementById(DOM_IDS.TABLE);
    state.elements.saveBtn = document.getElementById(DOM_IDS.BTN_SAVE);
    state.elements.addBtn = document.getElementById(DOM_IDS.BTN_ADD_GROUP);

    if (!state.elements.table) return;

    bindEvents();
    state.updateUiState();

    try {
        const data = await api.fetchMatrix();

        state.tabulator = new Tabulator(state.elements.table, {
            data: data.permissions,
            columns: buildColumns(data.groups),
            layout: 'fitData',
            height: 'calc(100vh - 35rem)',
            tooltipGenerationMode: 'hover',
            headerSort: false,
            reactiveData: false,
        });
    } catch (e) {
        console.error(e);
        addAlert('error', e.message);
    }
}
